import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Room, TaskObject, normalizeTask } from "@/types/checklist";
import { toast } from "@/hooks/use-toast";

interface TranslationItem {
  type: "room" | "task";
  text: string;
  roomIndex?: number;
  taskIndex?: number;
}

interface TranslationResult {
  type: "room" | "task";
  original: string;
  translated: string;
  roomIndex?: number;
  taskIndex?: number;
}

export const useChecklistTranslation = () => {
  const [isTranslating, setIsTranslating] = useState(false);

  const translateRooms = async (rooms: Room[]): Promise<Room[]> => {
    setIsTranslating(true);
    
    try {
      // Build items to translate
      const items: TranslationItem[] = [];
      
      rooms.forEach((room, roomIndex) => {
        // Add room name for translation
        if (room.name.trim()) {
          items.push({ type: "room", text: room.name.trim(), roomIndex });
        }
        
        // Add task texts for translation
        room.tasks.forEach((task, taskIndex) => {
          const taskObj = normalizeTask(task);
          if (taskObj.text.trim()) {
            items.push({ type: "task", text: taskObj.text.trim(), roomIndex, taskIndex });
          }
        });
      });

      if (items.length === 0) {
        return rooms;
      }

      const { data, error } = await supabase.functions.invoke("translate-checklist", {
        body: { items, targetLanguage: "es" },
      });

      if (error) {
        console.error("Translation error:", error);
        toast({
          title: "Translation Warning",
          description: "Could not translate some items. English will be used.",
          variant: "destructive",
        });
        return addEnglishFallbacks(rooms);
      }

      const translations: TranslationResult[] = data?.translations || [];
      
      // Apply translations to rooms
      const translatedRooms = rooms.map((room, roomIndex) => {
        // Find room name translation
        const roomTranslation = translations.find(
          (t) => t.type === "room" && t.roomIndex === roomIndex
        );
        
        const translatedTasks = room.tasks.map((task, taskIndex) => {
          const taskObj = normalizeTask(task);
          const taskTranslation = translations.find(
            (t) => t.type === "task" && t.roomIndex === roomIndex && t.taskIndex === taskIndex
          );
          
          return {
            ...taskObj,
            text_en: taskObj.text.trim(),
            text_es: taskTranslation?.translated || taskObj.text.trim(),
          };
        });

        return {
          ...room,
          name_en: room.name.trim(),
          name_es: roomTranslation?.translated || room.name.trim(),
          tasks: translatedTasks,
        };
      });

      return translatedRooms;
    } catch (err) {
      console.error("Translation failed:", err);
      toast({
        title: "Translation Warning",
        description: "Translation service unavailable. English will be used.",
        variant: "destructive",
      });
      return addEnglishFallbacks(rooms);
    } finally {
      setIsTranslating(false);
    }
  };

  return { translateRooms, isTranslating };
};

// Helper to add English fallbacks when translation fails
const addEnglishFallbacks = (rooms: Room[]): Room[] => {
  return rooms.map((room) => ({
    ...room,
    name_en: room.name.trim(),
    name_es: room.name.trim(), // Use English as fallback
    tasks: room.tasks.map((task) => {
      const taskObj = normalizeTask(task);
      return {
        ...taskObj,
        text_en: taskObj.text.trim(),
        text_es: taskObj.text.trim(), // Use English as fallback
      };
    }),
  }));
};
